import CLIENT from '../utils/restClient';
import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../utils/logger';
import { getApprovalGate, RETURN_APPROVAL_WARNING_MESSAGES } from '../utils/returnApprovalConfig';

export const MODULE_NAME = 'RETURN_APPROVALS';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

export const ACTION_TYPES = Object.freeze(['GET_STATUS', 'GET_STATUS_OK', 'GET_STATUS_FAIL', 'SEND', 'SEND_DONE', 'RESET']
  .reduce((types, name) => ({ ...types, [name]: `${MODULE_NAME}.${name}` }), {}));
const { GET_STATUS, GET_STATUS_OK, GET_STATUS_FAIL, SEND, SEND_DONE, RESET } = ACTION_TYPES;

// `requested` stays true after a successful Send until RESET (unmount or a new generation loaded).
const INITIAL_STATE = { loading: false, data: null, failed: false, errorStatus: null, sending: false, requested: false };

export const updateSubStateFromAction = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case GET_STATUS:
      return { ...state, loading: true, data: null, failed: false, errorStatus: null };
    case GET_STATUS_OK:
      return { ...state, loading: false, data: action.response };
    case GET_STATUS_FAIL:
      return { ...state, loading: false, failed: true, errorStatus: action.status };
    case SEND:
      return { ...state, sending: true };
    case SEND_DONE:
      return { ...state, sending: false, requested: state.requested || Boolean(action.requested) };
    case RESET:
      return INITIAL_STATE;
    default:
      return state;
  }
};

const clearMessages = (messageIds) => messageIds.forEach((messageId) => log.clearDictionaryError(messageId));

// The gate decides which warning banner (if any) a status shows above the buttons, like the Send result messages.
const showApprovalWarning = (statusState, showWarning) => {
  const warningMessage = getApprovalGate(statusState)?.warningMessage;
  if (showWarning && warningMessage) log.setDictionaryMessage(warningMessage);
};

export const API = {
  getStatus: (params, { showWarning = true } = {}) => (dispatch) => {
    dispatch({ type: GET_STATUS });
    clearMessages(RETURN_APPROVAL_WARNING_MESSAGES);
    return CLIENT.getReturnApprovalStatus(params)
      .then((response) => {
        showApprovalWarning({ data: response }, showWarning);
        return dispatch({ type: GET_STATUS_OK, response });
      })
      .catch((error) => {
        log.debug('getStatus(): failed', error);
        // A 503 shows the "can't be checked" warning banner (D16); other failures show the error toast.
        if (error?.status !== 503) log.setDictionaryMessage(ALL_MESSAGES.CONNECTION_ERROR);
        showApprovalWarning({ failed: true, errorStatus: error?.status }, showWarning);
        dispatch({ type: GET_STATUS_FAIL, status: error?.status || null });
      });
  },

  // Only this send's result banner (sent or failed) is shown: earlier approval banners are cleared first, and the
  // refetch below raises no warning (the new run starts asynchronously, so Status may still report the old one).
  send: (params) => (dispatch) => {
    dispatch({ type: SEND });
    clearMessages([ALL_MESSAGES.RETURN_APPROVAL_SENT, ALL_MESSAGES.RETURN_APPROVAL_SEND_FAILED, ...RETURN_APPROVAL_WARNING_MESSAGES]);
    return CLIENT.sendReturnForApproval(params)
      .then(() => {
        log.setDictionaryMessage(ALL_MESSAGES.RETURN_APPROVAL_SENT);
        return true;
      }, (error) => {
        log.debug('send(): failed', error);
        log.setDictionaryMessage(ALL_MESSAGES.RETURN_APPROVAL_SEND_FAILED);
        return false;
      })
      .then((requested) => {
        dispatch({ type: SEND_DONE, requested });
        return dispatch(API.getStatus(params, { showWarning: false }));
      });
  },

  reset: () => ({ type: RESET })
};

export default API;
